import {AgentIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {useCallback, useMemo} from 'react'
import {insertText} from '@github-ui/text'
import {useClickAnalytics} from '@github-ui/use-analytics'

// Inline constant to avoid importing from @github-ui/filter-core/providers
const AT_COPILOT_VALUE = '@copilot'

// Data attribute to store prepopulated hint text for similarity tracking
export const COPILOT_HINT_TEXT_ATTR = 'data-copilot-hint-text'

// Data attribute to store the comment anchor that the button logged
export const COPILOT_HINT_ANCHOR_ATTR = 'data-copilot-hint-anchor'

// Data attribute to store the unique hint ID that connects button click and comment submit telemetry
export const COPILOT_HINT_ID_ATTR = 'data-copilot-hint-id'

export interface CopilotApplyChangesButtonProps {
  commentPermalink: string
  isCopilotAuthoredPr?: boolean
  isOverviewComment?: boolean
  commentCount?: number
}

const extractCommentAnchor = (permalink: string): string => {
  // Extract comment anchor from permalink (e.g., "#issuecomment-123456" or "#discussion_r123456")
  const match = permalink.match(/#([^#\s]+)$/)
  return match?.[1] || 'unknown'
}

const generateHintId = (): string => {
  // Use the standard crypto.randomUUID() for proper UUID generation
  return crypto.randomUUID()
}

export const CopilotApplyChangesButton: React.FC<CopilotApplyChangesButtonProps> = ({
  commentPermalink,
  isCopilotAuthoredPr = true,
  isOverviewComment = false,
  commentCount = 1,
}) => {
  const commentAnchor = useMemo(() => extractCommentAnchor(commentPermalink), [commentPermalink])
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const buttonLabel = useMemo(() => {
    return commentCount > 1 ? 'Implement all suggestions' : 'Implement suggestion'
  }, [commentCount])
  const openCommentForm = useCallback(
    (buttonElement: HTMLButtonElement) => {
      if (isOverviewComment) {
        // For overview comments, find the main comment field directly
        // It could be by ID (new_comment_field) or by class (js-comment-field) in the document
        const commentField =
          document.getElementById('new_comment_field') ||
          document.querySelector('textarea.js-comment-field[name="comment[body]"]')

        if (commentField) {
          // Return the parent container (form or containing div)
          return commentField.closest('form') || commentField.parentElement
        }
        return null
      }

      // For inline comments, use the existing logic
      // Find the containing .js-line-comments element (table cell)
      const lineCommentsContainer = buttonElement.closest('.js-line-comments')
      if (!lineCommentsContainer) {
        return null
      }

      // Find the inline comment form container within the line comments
      const inlineFormContainer = lineCommentsContainer.querySelector('.js-inline-comment-form-container')
      if (!inlineFormContainer) {
        return null
      }

      // Use the same logic as GitHub's focusForm function
      const container = inlineFormContainer as HTMLElement
      container.classList.add('open')

      // Click the write tab if it exists
      const writeTab = container.querySelector('.js-write-tab')
      if (writeTab instanceof HTMLElement) {
        writeTab.click()
      }

      return container
    },
    [isOverviewComment],
  )

  const insertCopilotText = useCallback(
    (commentField: HTMLTextAreaElement, anchor: string, hintId: string) => {
      const action = isCopilotAuthoredPr ? '' : 'open a new pull request to '
      const feedbackReference = isOverviewComment
        ? `the comments in [this thread](${commentPermalink})`
        : `[this feedback](${commentPermalink})`
      const changeText = `${AT_COPILOT_VALUE} ${action}apply changes based on ${feedbackReference}`

      // Store the prepopulated text for similarity comparison on submit
      // This will be used by the telemetry element to compute hintSimilarity
      commentField.setAttribute(COPILOT_HINT_TEXT_ATTR, changeText)

      // Store the comment anchor that was logged by the button click
      // This will be used by the telemetry element to link button click and comment submit events
      commentField.setAttribute(COPILOT_HINT_ANCHOR_ATTR, anchor)

      // Store the unique hint ID that connects button click and comment submit telemetry
      commentField.setAttribute(COPILOT_HINT_ID_ATTR, hintId)

      // Use the established GitHub text insertion utility to append text, after clearing it
      commentField.value = '' // Clear existing content
      insertText(commentField, changeText)
    },
    [commentPermalink, isCopilotAuthoredPr, isOverviewComment],
  )

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      // Generate unique hint ID to connect button click and comment submit telemetry
      const hintId = generateHintId()

      // Send telemetry event for CCR reply hint clicked
      sendClickAnalyticsEvent({
        category: 'ccr_reply_hint_clicked',
        commentAnchor,
        hintId,
      })

      // Use the clicked button element directly instead of querying for it
      const buttonElement = event.currentTarget
      if (!buttonElement) {
        return
      }

      const container = openCommentForm(buttonElement)
      if (!container) {
        return
      }

      // Find and focus the comment field
      let commentField: HTMLTextAreaElement | null = null

      if (isOverviewComment) {
        // For overview comments, find the main comment field directly in the document
        commentField =
          (document.getElementById('new_comment_field') as HTMLTextAreaElement) ||
          (document.querySelector('textarea.js-comment-field[name="comment[body]"]') as HTMLTextAreaElement)
      } else {
        // For inline comments, look within the container
        commentField = container.querySelector('.js-comment-field') as HTMLTextAreaElement
      }

      if (commentField instanceof HTMLTextAreaElement) {
        insertCopilotText(commentField, commentAnchor, hintId)
        commentField.focus()
      }
    },
    [openCommentForm, insertCopilotText, sendClickAnalyticsEvent, commentAnchor, isOverviewComment],
  )

  return (
    <Button
      size="small"
      variant="default"
      leadingVisual={AgentIcon}
      onClick={handleClick}
      data-testid={`copilot-apply-changes-button-${commentAnchor}`}
    >
      {buttonLabel}
    </Button>
  )
}
